import { currencyName } from "../currencies/meta";
import { formatDate, type AppLocale } from "../i18n/format";
import type { Coverage } from "./coverage";

/**
 * What we say about a currency we cannot fully stand behind.
 *
 * THE ARABIC IS THE ORIGINAL. The English is translated from it, not the other
 * way round, because the audience most likely to meet these states reads
 * Arabic.
 *
 * TWO VOICES, deliberately different:
 *
 *   caveated  describes the CURRENCY, not our app. It is the permanent, normal
 *             condition for a currency with a parallel market — so it must not
 *             read as a warning or an apology. Stated once, plainly, as a fact
 *             a person can use.
 *
 *   withheld  describes OUR DECISION. The subject is "we", and the verb is a
 *             refusal: we will not guess. Without that, silence reads as a
 *             fault rather than a choice.
 *
 * NOTHING HERE MAY IMPLY "TEMPORARY". No "currently", no "try again later".
 * The Syrian pound conflict comes from a real redenomination and resolves only
 * if a provider fixes their data, which may never happen. A promise to return
 * shortly is a promise we do not control.
 *
 * Any date is passed through formatDate so it stays Gregorian with Latin
 * digits even inside an Arabic sentence — never interpolated raw.
 */

export interface CoverageCopy {
  /** One sentence. Shown on tap and in the currency info sheet. */
  readonly text: string;
  /** Present only when there is something to do about it. */
  readonly action?: { readonly label: string; readonly code: string };
}

export function coverageCopy(coverage: Coverage, locale: AppLocale): CoverageCopy | null {
  const ar = locale === "ar";

  switch (coverage.reason) {
    case "agree":
      return null;

    case "market-divergence":
      return {
        text: ar
          ? "هذا هو السعر الرسمي. قد تجد سعراً مختلفاً عند الصرافين."
          : "This is the official rate. Money changers may quote a different one.",
      };

    case "single-source":
      return {
        text: ar
          ? "مصدر واحد يقتبس هذه العملة، فليس لدينا ثانٍ نقارن به."
          : "Only one source quotes this currency, so we have nothing to check it against.",
      };

    case "no-source":
      return {
        text: ar
          ? "لا يصلنا سعر لهذه العملة من أي مصدر. لن نعرض رقماً نخمّنه."
          : "No source gives us a rate for this currency. We won't show a number we'd be guessing at.",
      };

    case "stale": {
      const when = coverage.asOf ? formatDate(coverage.asOf, locale) : null;
      return {
        text: ar
          ? when
            ? `آخر سعر وصلنا كان يوم ${when}، وهو أقدم من أن نعرضه اليوم.`
            : "آخر سعر وصلنا أقدم من أن نعرضه اليوم."
          : when
            ? `The last rate we received was ${when}, which is too old to show today.`
            : "The last rate we received is too old to show today.",
      };
    }

    case "sources-disagree":
      return {
        text: ar
          ? "مصادرنا لا تتفق على قيمة هذه العملة، غالباً لأن وحدتها تغيّرت مؤخراً. لا نعرف أيّ رقم هو الصحيح، فلن نختار."
          : "Our sources don't agree on what this currency is worth, usually because its unit changed recently. We don't know which figure is right, so we won't pick one.",
      };

    case "withdrawn": {
      const replacement = coverage.replacedBy;
      if (!replacement) {
        return {
          text: ar
            ? "سُحبت هذه العملة من التداول."
            : "This currency was withdrawn from circulation.",
        };
      }
      const name = currencyName(replacement, locale);
      return {
        text: ar
          ? `سُحبت هذه العملة من التداول. حلّ محلّها ${name} (${replacement}).`
          : `This currency was withdrawn from circulation. It was replaced by the ${name} (${replacement}).`,
        action: {
          label: ar ? `عرض ${replacement} بدلاً منها` : `Show ${replacement} instead`,
          code: replacement,
        },
      };
    }
  }
}

/** Short label for the persistent row marker's accessible name. */
export function coverageLabel(coverage: Coverage, locale: AppLocale): string {
  const ar = locale === "ar";
  if (coverage.state === "caveated") {
    return ar ? "ملاحظة عن هذا السعر" : "Note about this rate";
  }
  return ar ? "لا يوجد سعر لهذه العملة" : "No rate for this currency";
}
